import React, { useState, useEffect, useCallback } from 'react';
import {
  BriefcaseIcon,
  PlusIcon,
  XMarkIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  ChevronDownIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  getJobs,
  assignJobsToCandidate,
  getCandidateJobs,
  removeCandidateJob,
  updateCandidateJobStatus,
  recalculateCandidateJobScore,
  recalculateAllCandidateJobScores
} from '../services/api';

// Status configuration
const STATUS_CONFIG = {
  assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: BriefcaseIcon },
  emailed: { label: 'Emailed', color: 'bg-indigo-100 text-indigo-700', icon: CheckCircleIcon },
  interviewed: { label: 'Interviewed', color: 'bg-yellow-100 text-yellow-700', icon: CheckCircleIcon },
  offered: { label: 'Offered', color: 'bg-purple-100 text-purple-700', icon: CheckCircleIcon },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-600', icon: ExclamationTriangleIcon },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XMarkIcon }
};

// Match score badge component
const MatchScoreBadge = ({ score, size = 'md' }) => {
  let bgColor = 'bg-gray-100 text-gray-600';
  if (score >= 80) bgColor = 'bg-green-100 text-green-700';
  else if (score >= 60) bgColor = 'bg-yellow-100 text-yellow-700';
  else if (score >= 40) bgColor = 'bg-orange-100 text-orange-700';
  else if (score > 0) bgColor = 'bg-red-100 text-red-700';

  const sizeClasses = size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs';

  return (
    <span className={`rounded-full font-medium ${bgColor} ${sizeClasses}`}>
      {score}%
    </span>
  );
};

// Job assignment card component
const JobAssignmentCard = ({ assignment, onRemove, onStatusChange, onRecalculate, isRecalculating }) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const job = assignment.job;
  const statusConfig = STATUS_CONFIG[assignment.status] || STATUS_CONFIG.assigned;
  const StatusIcon = statusConfig.icon;

  const handleStatusChange = async (newStatus) => {
    setShowStatusMenu(false);
    await onStatusChange(assignment.jobId, newStatus);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-sm transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 text-sm truncate">
            {job?.title || 'Unknown Job'}
          </h4>
          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
            {job?.company && (
              <span className="flex items-center gap-1">
                <BuildingOfficeIcon className="h-3 w-3" />
                {job.company}
              </span>
            )}
            {job?.location && (
              <span className="flex items-center gap-1">
                <MapPinIcon className="h-3 w-3" />
                {job.location}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-2">
          <MatchScoreBadge score={assignment.matchScore} />
          <button
            onClick={() => onRemove(assignment.jobId)}
            className="text-gray-400 hover:text-red-500 transition-colors"
            title="Remove job assignment"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Recommendation */}
      {assignment.recommendation && assignment.recommendation !== 'Not Evaluated' && (
        <div className={`text-xs px-2 py-1 rounded mb-2 inline-block ${
          assignment.recommendation === 'Strong Match' ? 'bg-green-50 text-green-700' :
          assignment.recommendation === 'Moderate Match' ? 'bg-yellow-50 text-yellow-700' :
          'bg-red-50 text-red-700'
        }`}>
          {assignment.recommendation}
        </div>
      )}

      {/* Match Summary */}
      {assignment.matchSummary && (
        <p className="text-xs text-gray-600 mb-3 line-clamp-2">{assignment.matchSummary}</p>
      )}

      {/* Status and Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        {/* Status Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowStatusMenu(!showStatusMenu)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${statusConfig.color}`}
          >
            <StatusIcon className="h-3 w-3" />
            {statusConfig.label}
            <ChevronDownIcon className="h-3 w-3" />
          </button>

          {showStatusMenu && (
            <div className="absolute left-0 mt-1 w-36 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10">
              {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 ${
                    assignment.status === status ? 'bg-gray-50' : ''
                  }`}
                >
                  <config.icon className="h-3 w-3" />
                  {config.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recalculate button */}
        <button
          onClick={() => onRecalculate(assignment.jobId)}
          disabled={isRecalculating}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 disabled:opacity-50"
          title="Recalculate match score"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${isRecalculating ? 'animate-spin' : ''}`} />
          Recalculate
        </button>
      </div>
    </div>
  );
};

// Job selector modal
const JobSelectorModal = ({ isOpen, onClose, onAssign, existingJobIds, candidateName }) => {
  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await getJobs();
      // Filter out already assigned jobs
      const availableJobs = (response.data.jobs || response.data || []).filter(
        job => !existingJobIds.includes(job._id)
      );
      setJobs(availableJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const toggleJob = (jobId) => {
    setSelectedJobs(prev =>
      prev.includes(jobId)
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const handleAssign = async () => {
    if (selectedJobs.length === 0) return;
    setAssigning(true);
    try {
      await onAssign(selectedJobs);
      setSelectedJobs([]);
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setAssigning(false);
    }
  };

  const filteredJobs = jobs.filter(job =>
    job.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white rounded-lg max-w-lg w-full shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Assign Jobs</h3>
              <p className="text-sm text-gray-500">Select jobs to assign to {candidateName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Search */}
          <div className="px-4 py-3 border-b">
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Job List */}
          <div className="max-h-80 overflow-y-auto p-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading jobs...</div>
            ) : filteredJobs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {jobs.length === 0 ? 'No available jobs to assign' : 'No matching jobs found'}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredJobs.map(job => (
                  <div
                    key={job._id}
                    onClick={() => toggleJob(job._id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedJobs.includes(job._id)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 text-sm">{job.title}</h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          {job.company && <span>{job.company}</span>}
                          {job.location && <span>• {job.location}</span>}
                          {job.type && <span>• {job.type}</span>}
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedJobs.includes(job._id)
                          ? 'border-indigo-500 bg-indigo-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedJobs.includes(job._id) && (
                          <CheckCircleIcon className="h-4 w-4 text-white" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-sm text-gray-500">
              {selectedJobs.length} job{selectedJobs.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={selectedJobs.length === 0 || assigning}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {assigning ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4" />
                    Assign & Calculate Scores
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component
const CandidateJobsPanel = ({ candidateId, candidateName }) => {
  const [assignments, setAssignments] = useState([]);
  const [bestMatch, setBestMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [recalculatingJobId, setRecalculatingJobId] = useState(null);
  const [recalculatingAll, setRecalculatingAll] = useState(false);

  const fetchAssignments = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const response = await getCandidateJobs(candidateId);
      setAssignments(response.data.assignments || []);
      setBestMatch(response.data.bestMatch);
    } catch (error) {
      console.error('Error fetching candidate jobs:', error);
      toast.error('Failed to load job assignments');
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const handleAssignJobs = async (jobIds) => {
    try {
      const response = await assignJobsToCandidate(candidateId, jobIds);
      toast.success(`Successfully assigned ${response.data.assigned?.length || 0} job(s)`);
      await fetchAssignments();
    } catch (error) {
      console.error('Error assigning jobs:', error);
      toast.error(error.response?.data?.message || 'Failed to assign jobs');
      throw error;
    }
  };

  const handleRemoveJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to remove this job assignment?')) return;
    try {
      await removeCandidateJob(candidateId, jobId);
      toast.success('Job assignment removed');
      await fetchAssignments();
    } catch (error) {
      console.error('Error removing job:', error);
      toast.error('Failed to remove job assignment');
    }
  };

  const handleStatusChange = async (jobId, status) => {
    try {
      await updateCandidateJobStatus(candidateId, jobId, status);
      toast.success('Status updated');
      await fetchAssignments();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleRecalculate = async (jobId) => {
    setRecalculatingJobId(jobId);
    try {
      const response = await recalculateCandidateJobScore(candidateId, jobId);
      toast.success(`Score recalculated: ${response.data.assignment?.newScore}%`);
      await fetchAssignments();
    } catch (error) {
      console.error('Error recalculating score:', error);
      toast.error('Failed to recalculate score');
    } finally {
      setRecalculatingJobId(null);
    }
  };

  const handleRecalculateAll = async () => {
    if (!window.confirm('Recalculate match scores for all assigned jobs?')) return;
    setRecalculatingAll(true);
    try {
      const response = await recalculateAllCandidateJobScores(candidateId);
      toast.success(`Recalculated ${response.data.totalProcessed} job scores`);
      await fetchAssignments();
    } catch (error) {
      console.error('Error recalculating all scores:', error);
      toast.error('Failed to recalculate scores');
    } finally {
      setRecalculatingAll(false);
    }
  };

  const existingJobIds = assignments.map(a => a.jobId?._id || a.jobId);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <BriefcaseIcon className="h-4 w-4" />
          Assigned Jobs ({assignments.length})
        </h4>
        <div className="flex items-center gap-2">
          {assignments.length > 0 && (
            <button
              onClick={handleRecalculateAll}
              disabled={recalculatingAll}
              className="text-xs text-gray-500 hover:text-indigo-600 flex items-center gap-1"
            >
              <ArrowPathIcon className={`h-3.5 w-3.5 ${recalculatingAll ? 'animate-spin' : ''}`} />
              Recalculate All
            </button>
          )}
          <button
            onClick={() => setShowJobSelector(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
          >
            <PlusIcon className="h-4 w-4" />
            Assign Jobs
          </button>
        </div>
      </div>

      {/* Best Match Indicator */}
      {bestMatch && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <SparklesIcon className="h-5 w-5 text-green-600" />
          <div className="flex-1">
            <span className="text-sm font-medium text-green-800">Best Match: </span>
            <span className="text-sm text-green-700">{bestMatch.title} at {bestMatch.company}</span>
          </div>
          <MatchScoreBadge score={bestMatch.score} size="lg" />
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto mb-2" />
          Loading job assignments...
        </div>
      ) : assignments.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <BriefcaseIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-3">No jobs assigned yet</p>
          <button
            onClick={() => setShowJobSelector(true)}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4" />
            Assign First Job
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {assignments.map(assignment => (
            <JobAssignmentCard
              key={assignment.jobId?._id || assignment.jobId}
              assignment={assignment}
              onRemove={handleRemoveJob}
              onStatusChange={handleStatusChange}
              onRecalculate={handleRecalculate}
              isRecalculating={recalculatingJobId === (assignment.jobId?._id || assignment.jobId)}
            />
          ))}
        </div>
      )}

      {/* Job Selector Modal */}
      <JobSelectorModal
        isOpen={showJobSelector}
        onClose={() => setShowJobSelector(false)}
        onAssign={handleAssignJobs}
        existingJobIds={existingJobIds}
        candidateName={candidateName}
      />
    </div>
  );
};

export default CandidateJobsPanel;
